import type { BundledLanguage, BundledTheme, TokensResult } from 'shiki';
import { createHighlighterCoreSync, type HighlighterCore } from 'shiki/core';
import { createJavaScriptRegexEngine } from 'shiki/engine/javascript';

import githubDark from '@shikijs/themes/github-dark';
import githubLight from '@shikijs/themes/github-light';

import html from '@shikijs/langs/html';
import json from '@shikijs/langs/json';
import markdown from '@shikijs/langs/markdown';
import yaml from '@shikijs/langs/yaml';

type HighlightOptions = {
    code: string;
    language: BundledLanguage;
    themes: [string, string];
};

type CodeHighlighterPlugin = {
    name: 'shiki';
    type: 'code-highlighter';
    highlight: (options: HighlightOptions, callback?: (result: TokensResult) => void) => TokensResult | null;
    supportsLanguage: (language: BundledLanguage) => boolean;
    getSupportedLanguages: () => BundledLanguage[];
    getThemes: () => [BundledTheme, BundledTheme];
};

type CodePluginOptions = {
    themes?: [BundledTheme, BundledTheme];
};

const SUPPORTED_LANGUAGES: BundledLanguage[] = ['json', 'yaml', 'html', 'markdown'];

// Common aliases LLMs emit in code fences mapped to our canonical language ids.
const LANGUAGE_ALIASES: Record<string, string> = {
    yml: 'yaml',
    md: 'markdown',
};

const supportedSet = new Set<string>([...SUPPORTED_LANGUAGES, ...Object.keys(LANGUAGE_ALIASES)]);

const highlighter: HighlighterCore = createHighlighterCoreSync({
    themes: [githubLight, githubDark],
    langs: [json, yaml, html, markdown],
    engine: createJavaScriptRegexEngine(),
});

const resolveLanguage = (language: string): string => {
    const lower = (language ?? '').trim().toLowerCase();
    return LANGUAGE_ALIASES[lower] ?? lower;
};

export const createCodePlugin = (options: CodePluginOptions = {}): CodeHighlighterPlugin => {
    const themes: [BundledTheme, BundledTheme] = options.themes ?? ['github-light', 'github-dark'];
    const loadedLanguages = new Set(highlighter.getLoadedLanguages());

    return {
        name: 'shiki',
        type: 'code-highlighter',
        supportsLanguage: (language) => supportedSet.has(resolveLanguage(language)),
        getSupportedLanguages: () => [...SUPPORTED_LANGUAGES],
        getThemes: () => themes,
        highlight: ({ code, language, themes: requestedThemes }, callback) => {
            const resolved = resolveLanguage(language);
            const langToUse = loadedLanguages.has(resolved) ? resolved : 'text';
            let result: TokensResult;
            try {
                result = highlighter.codeToTokens(code, {
                    lang: langToUse,
                    themes: { light: requestedThemes[0], dark: requestedThemes[1] },
                });
            } catch {
                // codeToTokens throws on unloaded themes/langs (the contract returns
                // TokensResult | null). Returning null lets streamdown fall back to plain
                // text instead of crashing the message renderer.
                return null;
            }
            // Highlighting is always synchronous (createHighlighterCoreSync + preloaded langs),
            // so the result is returned directly. The plugin contract also exposes an async
            // callback path; fire it with the same tokens so streamdown still works if it
            // ever relies on the callback instead of the return value.
            callback?.(result);
            return result;
        },
    };
};

export const code = createCodePlugin();
